<template>
  <section class="contact-list document-list">
    <header>
      <mo-pageheader>
        <template slot="title">Contacts</template>
      </mo-pageheader>
    </header>

    <main class="main-layout">
      <!-- Recherche + Ajouter -->
      <div class="contact-toolbar flex flex-space">
        <div class="contact-search">
          <label class="search-label">Recherche (société, code société, nom)</label>
          <ba-input
            type="text"
            v-model="searchTerm"
            placeholder="Recherche (société, code société, nom)"
          ></ba-input>
        </div>
        <div class="contact-actions flex items-center">
          <div class="tooltip-container">
            <div class="contact-export" @click="handleExport"><ab-icon icon="excel"></ab-icon></div>
            <div class="tooltip">{{ $t('action.export') }}</div>
          </div>
          <mo-import-client-test></mo-import-client-test>
          <router-link :to="{ name: 'ContactNew' }" class="btn-action secondary contact-add">
            + Ajouter contact
          </router-link>
        </div>
      </div>

      <div class="loading" v-if="loading">
        <mo-loader :loading="loading"></mo-loader>
      </div>

      <ba-table v-if="contacts.length && !loading">
        <template slot="body">
          <tr v-for="(contact, index) in contacts" :key="index">
            <td>{{ contact.last_name }}</td>
            <td>
              <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a>
            </td>
            <td>{{ contact.phone }}</td>
            <td>{{ cliLivLabel(contact.cli_liv) }}</td>
            <td>{{ contact.company }}</td>
            <td>{{ contact.client_erp }}</td>
            <td>{{ contact.client_is_active ? 'O' : 'N' }}</td>
            <td>{{ contact.zip_code }}</td>
            <td>{{ contact.city }}</td>
            <td class="more filter-link flex flex-end">
              <div class="flex items-center">
                <div class="tooltip-container">
                  <router-link :to="{ name: 'ContactEdit', params: { id: contact.id } }" class="item">
                    <ab-icon icon="edit" iconset="contactgeneral"></ab-icon>
                  </router-link>
                  <div class="tooltip">{{ $t('action.edit') }}</div>
                </div>
                <div class="tooltip-container">
                  <span class="item" @click="showModal(contact.id)">
                    <ab-icon icon="delete" class="delete" iconset="contactgeneral"></ab-icon>
                  </span>
                  <div class="tooltip">{{ $t('action.delete') }}</div>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template slot="head">
          <tr>
            <th
              v-for="(col, index) in cols"
              :key="index"
              :class="col.sortable ? 'sortable' : ''"
              @click="sortBy(col)"
            >
              <span>{{ col.label }}<ab-icon :icon="sortIcon(col)" v-if="sortIcon(col)"></ab-icon></span>
            </th>
          </tr>
        </template>
      </ba-table>

      <div v-if="!contacts.length && !loading">
        <p class="no-data">Aucun contact disponible</p>
      </div>

      <footer class="mt12">
        <paginate
          :pageCount="pageCountTotal"
          prev-class="prev"
          next-class="next"
          prev-text=" < "
          next-text=" > "
          v-model="currentPage"
          v-if="contactsCount > 0 && !loading"
          :containerClass="'mo-pagination'"
        ></paginate>
      </footer>
    </main>

    <!-- EXPORT FILE MODAL -->
    <modal class="modal" name="download" width="376px">
      <ba-card>
        <template slot="header">
          <span>{{ $t('sectionTitle.downloadContacts') }}</span>
        </template>
        <template>
          <p>{{ $t('modal.documentReadyForDownload') }}</p>
        </template>
        <template slot="footer">
          <div class="buttons-container flex flex-center">
            <a class="btn-action primary"
               :href="download.href"
               target="_blank"
               :download="download.file"
               @click="$modal.hide('download')">
              {{ $t('action.download') }}
            </a>
          </div>
        </template>
      </ba-card>
    </modal>

    <!-- Confirmation de suppression -->
    <modal class="modal" name="removeContact" width="376px" @before-open="beforeOpen">
      <ba-card>
        <template slot="header">
          <span>Supprimer le contact</span>
        </template>
        <template>
          <p>Êtes-vous sûr de vouloir supprimer ce contact ? Il disparaîtra des dossiers où il est présent (une ligne de suppression sera ajoutée à leur historique).</p>
        </template>
        <template slot="footer">
          <div class="buttons-container flex flex-end">
            <ba-button class="primary" @click.native="closeModal">{{ $t('action.cancel') }}</ba-button>
            <ba-button class="danger" @click.native="handleRemoveContact">{{ $t('action.confirm') }}</ba-button>
          </div>
        </template>
      </ba-card>
    </modal>

    <ab-iconset iconset="contactgeneral">
      <g id="edit">
        <path d="M21.9632 1.07832C20.5253 -0.35944 18.1941 -0.35944 16.7562 1.07832L2.15936 15.6739C2.05933 15.7739 1.9871 15.8979 1.94939 16.034L0.0298562 22.9634C-0.0490855 23.2475 0.0311345 23.5517 0.239515 23.7604C0.448215 23.9687 0.752476 24.0489 1.0366 23.9703L7.96653 22.0507C8.10268 22.0129 8.22669 21.9407 8.32672 21.8407L22.9233 7.24481C24.3589 5.80609 24.3589 3.47704 22.9233 2.03832L21.9632 1.07832ZM3.9421 16.2057L15.8885 4.26L19.7413 8.11245L7.79459 20.0581L3.9421 16.2057ZM3.1725 17.7498L6.25059 20.828L1.99285 22.0075L3.1725 17.7498ZM21.7663 6.08796L20.8986 6.9556L17.0455 3.10282L17.9135 2.23518C18.7122 1.43656 20.0072 1.43656 20.8059 2.23518L21.7663 3.19518C22.5637 3.99475 22.5637 5.2887 21.7663 6.08796Z" />
      </g>
      <g id="delete">
        <path d="M19.875 3H15.75V2.25C15.75 1.00936 14.7406 0 13.5 0H10.5C9.25936 0 8.25 1.00936 8.25 2.25V3H4.125C3.09113 3 2.25 3.84113 2.25 4.875V7.5C2.25 7.91419 2.58581 8.25 3 8.25H3.40988L4.05783 21.857C4.11506 23.0587 5.10225 24 6.30525 24H17.6947C18.8978 24 19.885 23.0587 19.9422 21.857L20.5901 8.25H21C21.4142 8.25 21.75 7.91419 21.75 7.5V4.875C21.75 3.84113 20.9089 3 19.875 3ZM9.75 2.25C9.75 1.83647 10.0865 1.5 10.5 1.5H13.5C13.9135 1.5 14.25 1.83647 14.25 2.25V3H9.75V2.25ZM3.75 4.875C3.75 4.66823 3.91823 4.5 4.125 4.5H19.875C20.0818 4.5 20.25 4.66823 20.25 4.875V6.75C20.0189 6.75 4.7078 6.75 3.75 6.75V4.875ZM18.4439 21.7857C18.4248 22.1862 18.0957 22.5 17.6947 22.5H6.30525C5.90423 22.5 5.57517 22.1862 5.55614 21.7857L4.91156 8.25H19.0884L18.4439 21.7857Z" />
        <path d="M12 21C12.4142 21 12.75 20.6642 12.75 20.25V10.5C12.75 10.0858 12.4142 9.75 12 9.75C11.5858 9.75 11.25 10.0858 11.25 10.5V20.25C11.25 20.6642 11.5858 21 12 21Z" />
        <path d="M15.75 21C16.1642 21 16.5 20.6642 16.5 20.25V10.5C16.5 10.0858 16.1642 9.75 15.75 9.75C15.3358 9.75 15 10.0858 15 10.5V20.25C15 20.6642 15.3358 21 15.75 21Z" />
        <path d="M8.25 21C8.66419 21 9 20.6642 9 20.25V10.5C9 10.0858 8.66419 9.75 8.25 9.75C7.83581 9.75 7.5 10.0858 7.5 10.5V20.25C7.5 20.6642 7.83577 21 8.25 21Z" />
      </g>
    </ab-iconset>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Paginate from 'vuejs-paginate';
import { AbIcon, AbIconset, BaButton, BaCard, BaInput, BaTable } from '@ab/basic-components';
import MoLoader from '@/components/loader/mo-loader.vue';
import MoPageheader from '@/components/layout/nav/mo-pageheader.vue';
// Import client manuel : le composant gere lui-meme sa visibilite (env + compte)
import MoImportClientTest from '@/components/temp/mo-import-client-test.vue';

export default {
  name: 'ContactList',
  components: {
    AbIcon,
    AbIconset,
    BaButton,
    BaCard,
    BaInput,
    BaTable,
    MoImportClientTest,
    MoLoader,
    MoPageheader,
    Paginate
  },
  data() {
    return {
      loading: false,
      searchTerm: '',
      currentPage: 1,
      contactId: '',
      searchTimer: null,
      download: { href: '', file: '' },
      sortKey: '',
      sortDir: '',
      cols: [
        { label: 'Nom', key: 'last_name', sortable: true },
        { label: 'Email', key: 'email', sortable: true },
        { label: 'Téléphone', key: 'phone', sortable: true },
        { label: 'Type de contact', key: 'cli_liv', sortable: true },
        { label: 'Société', key: 'company', sortable: true },
        { label: 'Code client', key: 'client_erp', sortable: true },
        { label: 'Client actif', key: 'client_is_active', sortable: true },
        { label: 'Code Postal', key: 'zip_code', sortable: true },
        { label: 'Ville', key: 'city', sortable: true },
        { label: '', key: '', sortable: false }
      ]
    };
  },
  computed: {
    ...mapState({ contacts: state => state.contacts.allContacts }),
    ...mapState({ contactsCount: state => state.contacts.allContactsCount }),
    limit() {
      return Number(this.$store.getters.getLimit) || 30;
    },
    pageCountTotal() {
      return Math.ceil(this.contactsCount / this.limit) || 1;
    }
  },
  watch: {
    searchTerm() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.applyAndReload();
      }, 350);
    },
    currentPage() {
      this.getData();
    }
  },
  async mounted() {
    await this.getData();
  },
  methods: {
    cliLivLabel(cliLiv) {
      if (Number(cliLiv) === 1) return 'Contact client';
      if (Number(cliLiv) === 2) return 'Contact de livraison';
      return '';
    },
    async getData() {
      this.loading = true;
      try {
        await this.$store.dispatch('getAllContacts', {
          search: this.searchTerm.trim(),
          page: this.currentPage,
          limit: this.limit,
          sortKey: this.sortKey,
          sortDir: this.sortDir
        });
      } finally {
        this.loading = false;
        this.$store.commit('setSpinner', false);
      }
    },
    sortBy(col) {
      if (!col.sortable) return;
      if (this.sortKey !== col.key) {
        this.sortKey = col.key;
        this.sortDir = 'ASC';
      } else if (this.sortDir === 'ASC') {
        this.sortDir = 'DESC';
      } else {
        this.sortKey = '';
        this.sortDir = '';
      }
      this.applyAndReload();
    },

    sortIcon(col) {
      if (!col.sortable || this.sortKey !== col.key || !this.sortDir) return '';
      return this.sortDir === 'DESC' ? 'desc' : 'asc';
    },
    applyAndReload() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.getData();
      }
    },
    async handleExport() {
      this.$store.commit('setSpinner', true);
      try {
        const data = await this.$store.dispatch('exportContacts', {
          search: this.searchTerm.trim(),
          sortKey: this.sortKey,
          sortDir: this.sortDir
        });
        if (data) {
          const date = new Date();
          this.download.href = 'data:attachment/csv;charset=utf-8,' + encodeURIComponent(data);
          this.download.file = `contacts-${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}.csv`;
          this.$modal.show('download', {});
        }
      } finally {
        this.$store.commit('setSpinner', false);
      }
    },
    showModal(id) {
      this.$modal.show('removeContact', { id: id });
    },
    beforeOpen(e) {
      this.contactId = e.params.id;
    },
    closeModal() {
      this.$modal.hide('removeContact');
    },
    async handleRemoveContact() {
      if (this.contactId) {
        const data = await this.$store.dispatch('deleteContact', this.contactId);
        if (data) {
          this.$store.dispatch('displaySnackbar', { text: 'Le contact a été supprimé' });
          if (this.contacts.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          } else {
            await this.getData();
          }
        }
        this.closeModal();
      }
    }
  }
};
</script>

<style scoped>
.contact-toolbar {
  margin-top: 2rem;
  margin-bottom: 1rem;
  align-items: flex-end;
}
.contact-search {
  flex: 1;
  max-width: 640px;
}
.contact-search .search-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}
.contact-add {
  white-space: nowrap;
  margin-left: 1rem;
}
.contact-export {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.bar:before, .bar:after,
.bar-full:before, .bar-full:after {
  background: transparent;
}

.contact-list .ba-table { margin-top: 0.5rem; }
.contact-list .ba-table th.sortable { cursor: pointer; user-select: none; }
.more .delete { color: var(--ba-button-bg-danger, #d9534f); }
.mt12 { margin-top: 0.79rem; }
.no-data { margin-top: 2rem; text-align: center; }
</style>

<style>
/* Le tableau Contacts n'a pas d'image en 1re colonne : on aligne la 1re cellule
   comme les autres. On annule à la fois le centrage global
   (#app .ba-table td:first-child { text-align: center } dans main.css) et le
   padding réduit du composant (.ba-table tbody tr td:first-child { padding: 5px 5px }). */
#app .contact-list .ba-table tbody tr td:first-child {
  text-align: left;
  padding: 20px 10px;
}
</style>
<style src="@/assets/styles/documents.css"></style>
<style src="@/assets/styles/modals.css"></style>
<style src="@/assets/styles/paginations.css"></style>
<style src="@/assets/styles/tooltips.css"></style>
